import React, { memo, useEffect, useState } from 'react'
import { useGeocodingSearch } from 'transfix-maps/hooks/useGeocodingSearch'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import {
  Autosuggest,
  getAutosuggestOptions,
  getInputIcon,
  IAutosuggestWrapperProps,
} from 'transfix-ui/components/Autosuggest'
import { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { getLabelFromLocation } from 'apps/fleet-planner/components/atoms/FacilityAutosuggest'
import { getEntityById } from 'utils/getEntityById'

/**
 * Formats incoming value, and gives it a label
 */
export const getDefaultValue = (
  val: Partial<ILocationSuggestion> | undefined
): Partial<ILocationSuggestion> => {
  const { city, id, state } = val || {}
  return {
    city,
    state,
    label: getLabelFromLocation({
      city,
      state,
    }),
    id,
  }
}

/**
 * This type ensures that id, label, city, and state are all defined.
 * All of these are necessary to display and select a valid location.
 */
export interface ILocationAutosuggestValue
  extends Required<Pick<ILocationSuggestion, 'id' | 'city' | 'label' | 'state'>> {}

export interface ILocationAutosuggestProps
  extends IAutosuggestWrapperProps<ILocationAutosuggestValue> {}

export const LocationAutosuggest = memo<ILocationAutosuggestProps>(
  ({
    dataTest = 'location-autosuggest',
    disabled: disabledProp,
    error: errorProp,
    highlighted = false,
    icon: iconProp = 'locationPin',
    onAddEntity: onAddEntityProp,
    onBlur: onBlurProp,
    onCancel: onCancelProp,
    onChange: onChangeProp,
    onFocus: onFocusProp,
    required: requiredProp = false,
    value,
  }) => {
    const { isMobile } = useBreakpointContext()

    // Mirrors actual value, but triggers API calls and can be used for validation
    const [inputValue, setInputValue] = useState<string>('')

    const { results: mapResults = [], getResults } = useGeocodingSearch({
      debounce: 250,
      getResultLabel: ({ city, stateCode }) => {
        return [city, stateCode].filter(Boolean).join(', ')
      },
    })

    const results: ILocationSuggestion[] = [
      ...mapResults
        .filter(r => r?.label !== value?.label)
        .map(result => {
          const { stateCode } = result || {}
          return {
            ...result,
            state: stateCode ?? '',
          }
        }),
    ]

    // Make sure that current value is always selectable
    if (value?.id) results.unshift(value)

    const changeHandler = (option: IAutocompleteOption | undefined) => {
      const location = getEntityById(results, option?.id)
      onChangeProp(location)
    }

    const inputChangeHandler = (val: string = '') => {
      setInputValue(val)
    }

    // Update query search when input updates
    useEffect(() => {
      async function fetchResults() {
        await getResults(inputValue)
      }

      // Prevents fetch on mount
      if (!!inputValue && inputValue !== value?.label) {
        fetchResults()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue])

    const options = getAutosuggestOptions({
      options: results,
      groupIndex: 1,
      groupName: 'Location',
      iconName: 'LocationPinIcon',
      inputValue,
      mapOption: ({ id = '', label: text = '' }) => ({ id, text }),
    })

    const icon = getInputIcon(iconProp, inputValue)

    return (
      <Autosuggest
        highlighted={highlighted}
        id='location'
        label='Location'
        placeholder='Search by city, state, or facility'
        onAddEntity={onAddEntityProp}
        value={value}
        options={options}
        onCancel={onCancelProp}
        onChange={changeHandler}
        onInputChange={inputChangeHandler}
        renderOptionType='withIcon'
        icon={icon}
        clearable
        dataTest={dataTest}
        error={errorProp}
        onBlur={onBlurProp}
        disabled={disabledProp}
        selectOnFocus={!isMobile}
        onFocus={onFocusProp}
        required={requiredProp}
        renderGroupType='withoutGroupName'
      />
    )
  }
)

export default LocationAutosuggest
